(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.RFAWishlistState = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const BUCKET_COUNT = 8;
  const BUCKET_SIZE = 128;
  const CAPACITY = BUCKET_COUNT * BUCKET_SIZE;

  function isProductGid(value) {
    return typeof value === 'string' && /^gid:\/\/shopify\/Product\/[^/]+$/.test(value);
  }

  function arraysEqual(left, right) {
    return left.length === right.length && left.every((value, index) => value === right[index]);
  }

  function bucketsEqual(left, right) {
    return Array.from({ length: BUCKET_COUNT }, (_, index) =>
      arraysEqual(left[index] || [], right[index] || [])
    ).every(Boolean);
  }

  function flattenBuckets(buckets) {
    const seen = new Set();
    const items = [];

    (buckets || []).forEach((bucket) => {
      (Array.isArray(bucket) ? bucket : []).forEach((gid) => {
        if (!isProductGid(gid) || seen.has(gid)) return;
        seen.add(gid);
        items.push(gid);
      });
    });

    return items.slice(0, CAPACITY);
  }

  function bucketize(items) {
    const normalized = flattenBuckets([items]);
    return Array.from({ length: BUCKET_COUNT }, (_, index) =>
      normalized.slice(index * BUCKET_SIZE, (index + 1) * BUCKET_SIZE)
    );
  }

  function normalizeBuckets(buckets) {
    return bucketize(flattenBuckets(buckets));
  }

  function parseValue(value) {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(isProductGid) : [];
    } catch (_error) {
      return [];
    }
  }

  function snapshotFromApi(customer) {
    const originalBuckets = [];
    const rawBuckets = [];
    const digests = [];

    for (let index = 1; index <= BUCKET_COUNT; index += 1) {
      const alias = `wishlist${String(index).padStart(2, '0')}`;
      const metafield = customer?.[alias] || null;
      const raw = parseValue(metafield?.value);

      originalBuckets.push(raw);
      rawBuckets.push(raw);
      digests.push(metafield?.compareDigest ?? null);
    }

    const buckets = normalizeBuckets(rawBuckets);
    return {
      ownerId: customer?.id || null,
      buckets,
      digests,
      dirty: !bucketsEqual(originalBuckets, buckets),
    };
  }

  function removeProducts(buckets, productIds) {
    const removedIds = new Set((productIds || []).filter(isProductGid));
    const normalized = normalizeBuckets(buckets);
    const items = flattenBuckets(normalized);
    const remaining = items.filter((productId) => !removedIds.has(productId));

    return {
      buckets: bucketize(remaining),
      changed: remaining.length !== items.length,
      removed: items.filter((productId) => removedIds.has(productId)),
    };
  }

  function restoreInsertIndex(items, position) {
    const nextProductId = position?.nextProductId;
    if (isProductGid(nextProductId)) {
      const nextIndex = items.indexOf(nextProductId);
      if (nextIndex !== -1) return nextIndex;
    }

    const previousProductId = position?.previousProductId;
    if (isProductGid(previousProductId)) {
      const previousIndex = items.indexOf(previousProductId);
      if (previousIndex !== -1) return previousIndex + 1;
    }

    const originalIndex = Number.parseInt(position?.originalIndex, 10);
    if (Number.isInteger(originalIndex) && originalIndex >= 0) {
      return Math.min(originalIndex, items.length);
    }

    return 0;
  }

  function applyOperation(buckets, productId, action, restorePosition = null) {
    if (!isProductGid(productId)) throw new TypeError('A Shopify Product GID is required.');
    if (action !== 'add' && action !== 'remove') throw new TypeError('Action must be add or remove.');

    const normalized = normalizeBuckets(buckets);
    const items = flattenBuckets(normalized);
    const existingIndex = items.indexOf(productId);

    if (action === 'add') {
      if (existingIndex !== -1) {
        return { buckets: normalized, changed: false, duplicate: true, capacityReached: false };
      }
      if (items.length >= CAPACITY) {
        return { buckets: normalized, changed: false, duplicate: false, capacityReached: true };
      }
      if (restorePosition) items.splice(restoreInsertIndex(items, restorePosition), 0, productId);
      else items.unshift(productId);
    } else {
      if (existingIndex === -1) {
        return { buckets: normalized, changed: false, duplicate: false, capacityReached: false };
      }
      items.splice(existingIndex, 1);
    }

    return {
      buckets: bucketize(items),
      changed: true,
      duplicate: false,
      capacityReached: false,
    };
  }

  function isCasConflict(error) {
    const code = String(error?.code || '');
    const message = String(error?.message || '');
    return /COMPARE|DIGEST|STALE|CONFLICT/i.test(`${code} ${message}`);
  }

  async function runCas({ read, transform, write, maxRetries = 3, conflict = isCasConflict }) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries + 1; attempt += 1) {
      const snapshot = await read();
      const result = await transform(snapshot);

      if (!result.changed) return { ...result, snapshot, attempts: attempt };

      try {
        await write(snapshot, result);
        return { ...result, snapshot, attempts: attempt };
      } catch (error) {
        lastError = error;
        if (!conflict(error) || attempt > maxRetries) throw error;
      }
    }

    throw lastError;
  }

  return {
    BUCKET_COUNT,
    BUCKET_SIZE,
    CAPACITY,
    applyOperation,
    bucketize,
    bucketsEqual,
    flattenBuckets,
    isCasConflict,
    isProductGid,
    normalizeBuckets,
    removeProducts,
    runCas,
    snapshotFromApi,
  };
});
